// styled.ts
import styled from "styled-components";

import { Badge as ShadcnBadge } from "@/components/ui/badge";
import {
    Card as ShadcnCard,
    CardContent as ShadcnCardContent,
} from "@/components/ui/card";
import {
    Tabs as ShadcnTabs,
    TabsContent as ShadcnTabsContent,
    TabsList as ShadcnTabsList,
    TabsTrigger as ShadcnTabsTrigger,
} from "@/components/ui/tabs";
import { device } from "@/utils/sizes";
import { colourScheme, hexToRgba } from "@/utils/styling/colours";
// Helper function for responsive font sizes
const responsiveFontSize = (desktopSize: string, mobileSize: string) => `
  font-size: ${mobileSize};
  ${device.md} {
    font-size: ${desktopSize};
  }
`;

// Helper function for responsive paddings
const responsivePadding = (desktopPadding: string, mobilePadding: string) => `
  padding: ${mobilePadding};
  ${device.md} {
    padding: ${desktopPadding};
  }
`;

export const Card = styled(ShadcnCard)`
    width: 100%;
    max-width: 40rem;
    box-shadow:
        0 10px 15px -3px ${colourScheme.shadow},
        0 4px 6px -2px ${colourScheme.shadow};
    border: 1px solid ${colourScheme.secondaryDark};
    overflow: hidden;
    background: linear-gradient(
        135deg,
        ${colourScheme.darkGradientStart},
        ${colourScheme.darkGradientEnd}
    );
    color: ${colourScheme.white};
`;

export const CardContent = styled(ShadcnCardContent)`
    padding: 1rem;
`;

export const Tabs = styled(ShadcnTabs)`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

export const TabsList = styled(ShadcnTabsList)`
    display: flex;
    flex-wrap: wrap;
    flex: 1;
    width: 100%;
    border-radius: 0.5rem;
    padding: 0.25rem;
    margin-bottom: 1rem;
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    backdrop-filter: blur(4px);
`;

export const TabsTrigger = styled(ShadcnTabsTrigger)`
    flex: 1 1 auto; /* Allow flex items to shrink and wrap */
    padding: 0.375rem 0.5rem;
    ${responsiveFontSize("0.75rem", "0.7rem")};
    font-weight: 500;
    transition: all 0.3s;
    border-radius: 0.375rem;
    color: ${colourScheme.secondaryLight};
    border: 1px solid ${colourScheme.secondaryDark};
    text-align: center;
    box-sizing: border-box;
    margin: 0.25rem 0; /* Add vertical margin to separate rows */

    &.active {
        background-color: ${colourScheme.secondary};
        color: ${colourScheme.fullWhite};
        box-shadow:
            0 4px 6px -1px ${colourScheme.shadow},
            0 2px 4px -1px ${colourScheme.shadow};
    }
`;

export const TabsContent = styled(ShadcnTabsContent)`
    gap: 0.5rem;
    &.hidden {
        display: none;
    }
    &.visible {
        display: block;
    }
`;

export const ContentContainer = styled.div`
    flex: 1;
    padding-right: 0.5rem;
`;

export const Badge = styled(ShadcnBadge)`
    padding: 0.125rem 0.5rem;
    font-size: 0.75rem;
    color: ${colourScheme.white};
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
    background: linear-gradient(
        135deg,
        ${colourScheme.secondary},
        ${colourScheme.secondaryDark}
    );
`;

// Styled components for CompactList
export const CompactListContainer = styled.div`
    margin-bottom: 1rem;
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    border-radius: 0.5rem;
    ${responsivePadding("0.75rem", "0.5rem")};
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
    backdrop-filter: blur(4px);
    transition: all 0.3s;
    &:hover {
        box-shadow:
            0 10px 15px -3px ${colourScheme.shadow},
            0 4px 6px -2px ${colourScheme.shadow};
        background-color: ${hexToRgba(colourScheme.primaryLight, 0.7)};
    }
`;

export const CompactListButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: left;
    font-weight: 600;
    transition: color 0.3s;
    color: ${colourScheme.secondaryLight};
    background: none;
    border: none;
    cursor: pointer;
`;

export const TitleContainer = styled.span`
    display: flex;
    align-items: center;
`;

export const TitleText = styled.span`
    margin-left: 0.5rem;
    ${responsiveFontSize("0.875rem", "0.8rem")};
`;

export const List = styled.ul`
    margin-top: 0.5rem;
    padding: 0.5rem;
`;

export const ListItem = styled.li`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    color: ${colourScheme.white};
    &:not(:first-child) {
        margin-top: 0.25rem;
    }
`;

// Styled components for StatBar
export const StatBarContainer = styled.div`
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    ${responsivePadding("0.75rem", "0.5rem")};
    border-radius: 0.5rem;
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
    backdrop-filter: blur(4px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s;
    &:hover {
        box-shadow:
            0 10px 15px -3px ${colourScheme.shadow},
            0 4px 6px -2px ${colourScheme.shadow};
        background-color: ${hexToRgba(colourScheme.primary, 0.7)};
    }
`;

export const StatBarIconLarge = styled.div`
    font-size: 2rem; /* Matches 'text-3xl' */
    margin-bottom: 0.5rem; /* Matches 'mb-2' */
`;

export const StatBarIcon = styled.div`
    margin-bottom: 0.5rem;
`;

export const StatBarTextContainer = styled.div`
    text-align: center;
`;

export const StatBarName = styled.span`
    ${responsiveFontSize("1rem", "0.9rem")};
    font-weight: 500;
    margin-bottom: 0.25rem;
    color: ${colourScheme.secondaryLight};
`;

export const StatBarValue = styled.span`
    ${responsiveFontSize("1.125rem", "1rem")};
    font-weight: bold;
    color: ${colourScheme.white};
`;

export const StatBarValueContainer = styled.div`
    text-align: center;
`;

export const StatBarModifier = styled.span`
    ${responsiveFontSize("0.875rem", "0.8rem")};
    margin-left: 0.25rem; /* Matches 'ml-1' */
    color: ${colourScheme.greyLight};
`;

// Styled components for StatGrid
export const StatGridContainer = styled.div`
    margin-bottom: 1rem;
`;

export const StatGridTitle = styled.h3`
    font-weight: 600;
    margin-bottom: 0.5rem;
    ${responsiveFontSize("0.875rem", "0.8rem")};
    color: ${colourScheme.secondaryLight};
`;

export const StatGridGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;

    @media (max-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 400px) {
        grid-template-columns: 1fr;
    }
`;

// Styled components for SkillList
export const SkillListGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;

    ${device.sm} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

export const SkillItem = styled.div`
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    padding: 0.5rem;
    border-radius: 0.5rem;
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s;
    &:hover {
        box-shadow:
            0 10px 15px -3px ${colourScheme.shadow},
            0 4px 6px -2px ${colourScheme.shadow};
        background-color: ${hexToRgba(colourScheme.primaryLight, 0.7)};
    }
`;

export const SkillName = styled.span`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    font-weight: 500;
    color: ${colourScheme.secondaryLight};
`;

export const SkillValue = styled.span`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    font-weight: bold;
    color: ${colourScheme.white};
`;

// Styled components for Header
export const HeaderContainer = styled.div`
    margin-bottom: 1rem;
    text-align: center;
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    ${responsivePadding("0.75rem", "0.5rem")};
    border-radius: 0.5rem;
    backdrop-filter: blur(4px);
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
`;

export const HeaderTitle = styled.h2`
    ${responsiveFontSize("1.5rem", "1.25rem")};
    font-weight: bold;
    margin-bottom: 0.25rem;
    color: ${colourScheme.secondaryLight};
`;

export const HeaderLevel = styled.p`
    ${responsiveFontSize("0.875rem", "0.8rem")};
    font-weight: 600; /* Matches 'font-semibold' */
    color: ${colourScheme.secondary};
    margin-bottom: 0.25rem;
`;

export const HeaderSubtitle = styled.p`
    ${responsiveFontSize("0.875rem", "0.8rem")};
    color: ${colourScheme.white};
`;

// Styled components for InfoCard
export const InfoCard = styled.div`
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    padding: 0.5rem;
    border-radius: 0.5rem;
    margin: 0.5rem 0;
    border: 1px solid ${colourScheme.primaryLight};
    box-shadow:
        0 4px 6px -1px ${colourScheme.shadow},
        0 2px 4px -1px ${colourScheme.shadow};
    backdrop-filter: blur(4px);
    transition: all 0.3s;
    &:hover {
        box-shadow:
            0 10px 15px -3px ${colourScheme.shadow},
            0 4px 6px -2px ${colourScheme.shadow};
        background-color: ${hexToRgba(colourScheme.primaryLight, 0.7)};
    }
`;

export const InfoTitle = styled.h3`
    font-weight: 600;
    margin-bottom: 0.25rem;
    ${responsiveFontSize("0.75rem", "0.7rem")};
    color: ${colourScheme.secondaryLight};
`;

export const InfoText = styled.p`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    color: ${colourScheme.white};
`;

export const SectionTitle = styled.h3`
    font-weight: 600;
    margin-bottom: 0.5rem;
    ${responsiveFontSize("0.875rem", "0.8rem")};
    color: ${colourScheme.secondaryLight};
`;

// Other styled components as needed
export const GridOneColumn = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
`;

export const GridTwoColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

export const GridThreeColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

export const ListDisc = styled.ul`
    list-style-type: disc;
    list-style-position: inside;
`;

export const EquipmentCard = styled.div`
    background-color: ${hexToRgba(colourScheme.primaryLight, 0.5)};
    padding: 0.5rem;
    border-radius: 0.5rem;
    margin: 0.5rem 0;
    box-shadow: 0 1px 2px ${colourScheme.shadow};
    backdrop-filter: blur(4px);
    transition: all 0.3s;
    &:hover {
        box-shadow: 0 2px 4px ${colourScheme.shadow};
        background-color: ${hexToRgba(colourScheme.primaryLight, 0.7)};
    }
`;

export const EquipmentTitle = styled.h3`
    font-weight: 600;
    ${responsiveFontSize("0.75rem", "0.7rem")};
    color: ${colourScheme.secondaryLight};
`;

export const EquipmentDescription = styled.p`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    margin-top: 0.25rem;
    color: ${colourScheme.white};
`;

export const EquipmentValueContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
`;

export const EquipmentValueText = styled.span`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    color: ${colourScheme.alert};
    margin-left: 0.25rem;
`;

export const ActionCard = styled.div`
    background-color: ${hexToRgba(colourScheme.primaryDark, 0.5)};
    padding: 0.5rem;
    border-radius: 0.5rem;
`;

export const ActionName = styled.p`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    font-weight: 600;
    color: ${colourScheme.white};
`;

export const ActionDescription = styled.p`
    ${responsiveFontSize("0.75rem", "0.7rem")};
    margin-top: 0.25rem;
    color: ${colourScheme.greyLight};
`;
